import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { ReadinessGuide } from "./constants";

/**
 * Who does an item, and a link to the page where it is done. The link is left out when
 * the viewer may not open that page, so it never lands on Permission Denied.
 */
export function ReadinessGuideLink({ guide }: Readonly<{ guide: ReadinessGuide }>) {
  const role = useUserRole();
  const canOpenPage = !guide.access || featureAccess[guide.access](role);

  return (
    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
      <Typography variant="body2" color="text.secondary">
        {guide.owner}
      </Typography>
      {canOpenPage && (
        <Button
          component={Link}
          to={guide.path}
          size="small"
          endIcon={<NavigateNextIcon />}
        >
          {guide.linkLabel}
        </Button>
      )}
    </Stack>
  );
}

/** A list of things to do, each with its owner and page. */
export default function ReadinessGuideList({
  guides,
}: Readonly<{ guides: ReadinessGuide[] }>) {
  return (
    <Stack gap={1}>
      {guides.map((guide) => (
        <Stack
          key={guide.label}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          gap={1}
        >
          <Typography variant="body2" fontWeight={600}>
            {guide.label}
          </Typography>
          <ReadinessGuideLink guide={guide} />
        </Stack>
      ))}
    </Stack>
  );
}
